import * as signalR from "@microsoft/signalr";

export class SignalRService {
  constructor(baseUrl = 'http://localhost:5272', apiKey = '') {
    this.baseUrl = baseUrl;
    this.apiKey = apiKey || process.env.REACT_APP_API_KEY || 'dev-api-key-12345';
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${baseUrl}/chathub?api_key=${encodeURIComponent(this.apiKey)}`, {
        transport: signalR.HttpTransportType.WebSockets | 
                  signalR.HttpTransportType.ServerSentEvents | 
                  signalR.HttpTransportType.LongPolling,
        skipNegotiation: false,
        headers: {
          'X-API-Key': this.apiKey
        }
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: retryContext => {
          console.log(`SignalR reconnect attempt ${retryContext.previousRetryCount + 1}`);
          if (retryContext.previousRetryCount === 0) return 0;
          if (retryContext.previousRetryCount === 1) return 2000;
          if (retryContext.previousRetryCount === 2) return 10000;
          if (retryContext.previousRetryCount === 3) return 30000;
          return null;
        }
      })
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.setupEventHandlers();
  }

  setupEventHandlers() {
    this.connection.onreconnecting((error) => {
      console.log('SignalR connection reconnecting...', error);
      this.reconnectAttempts++;
      if (this.onReconnectingCallback) {
        this.onReconnectingCallback(error);
      }
    });

    this.connection.onreconnected((connectionId) => {
      console.log('SignalR connection reconnected. Connection ID:', connectionId);
      this.reconnectAttempts = 0;
      if (this.onReconnectedCallback) {
        this.onReconnectedCallback(connectionId);
      }
    });

    this.connection.onclose((error) => {
      console.error('SignalR connection closed:', error);
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        console.error('Max reconnection attempts reached. Please refresh the page.');
      }
      if (this.onConnectionClosedCallback) {
        this.onConnectionClosedCallback(error, this.reconnectAttempts >= this.maxReconnectAttempts);
      }
    });
  }

  async start() {
    try {
      console.log(`Connecting to SignalR hub at: ${this.baseUrl}/chatHub`);
      await this.connection.start();
      console.log('✅ SignalR connection established successfully');
      console.log('Connection ID:', this.connection.connectionId);
      this.reconnectAttempts = 0;
    } catch (error) {
      console.error('❌ Error starting SignalR connection:', error);
      
      if (error instanceof Error) {
        if (error.message.includes('Failed to start the connection')) {
          throw new Error('Cannot connect to chat service. Please ensure the API server is running on http://localhost:5272 and accessible.');
        } else if (error.message.includes('CORS')) {
          throw new Error('CORS error: The API server CORS policy needs to be configured for React integration.');
        } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
          throw new Error('Authentication failed: Please check your API key. Use "dev-api-key-12345" for local development.');
        }
      }
      
      throw new Error(`Failed to connect to chat service: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  async stop() {
    try {
      await this.connection.stop();
      console.log('SignalR connection stopped');
    } catch (error) {
      console.error('Error stopping SignalR connection:', error);
    }
  }

  get isConnected() {
    return this.connection.state === signalR.HubConnectionState.Connected;
  }

  get connectionState() {
    switch (this.connection.state) {
      case signalR.HubConnectionState.Disconnected:
        return 'Disconnected';
      case signalR.HubConnectionState.Connecting:
        return 'Connecting';
      case signalR.HubConnectionState.Connected:
        return 'Connected';
      case signalR.HubConnectionState.Disconnecting:
        return 'Disconnecting';
      case signalR.HubConnectionState.Reconnecting:
        return 'Reconnecting';
      default:
        return 'Unknown';
    }
  }

  async joinSession(sessionId) {
    if (!this.isConnected) {
      throw new Error(`SignalR connection is not established. Current state: ${this.connectionState}`);
    }
    console.log(`Joining session: ${sessionId}`);
    await this.connection.invoke("JoinSession", sessionId);
  }

  async leaveSession(sessionId) {
    if (!this.isConnected) return;
    console.log(`Leaving session: ${sessionId}`);
    await this.connection.invoke("LeaveSession", sessionId);
  }

  async sendMessage(sessionId, message) {
    if (!this.isConnected) {
      throw new Error(`SignalR connection is not established. Current state: ${this.connectionState}`);
    }
    console.log(`Sending message to session ${sessionId}:`, message);
    await this.connection.invoke("SendMessage", sessionId, message);
  }

  onSessionJoined(callback) {
    this.connection.on("SessionJoined", callback);
  }

  onMessageReceived(callback) {
    this.connection.on("MessageReceived", callback);
  }

  onMessageSent(callback) {
    this.connection.on("MessageSent", callback);
  }

  onError(callback) {
    this.connection.on("Error", callback);
  }

  onReconnecting(callback) {
    this.onReconnectingCallback = callback;
  }

  onReconnected(callback) {
    this.onReconnectedCallback = callback;
  }

  onConnectionClosed(callback) {
    this.onConnectionClosedCallback = callback;
  }

  removeAllListeners() {
    this.connection.off("SessionJoined");
    this.connection.off("MessageReceived");
    this.connection.off("MessageSent");
    this.connection.off("Error");
    
    // Clear reconnection callbacks
    this.onReconnectingCallback = null;
    this.onReconnectedCallback = null;
    this.onConnectionClosedCallback = null;
  }

  get connectionId() {
    return this.connection.connectionId;
  }

  async testConnection() {
    try {
      if (!this.isConnected) {
        await this.start();
      }
      return true;
    } catch (error) {
      console.error('Connection test failed:', error);
      return false;
    }
  }
}
